import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Phone, CheckCircle2 } from 'lucide-react';
import DynamicIsland from '../components/DynamicIsland';
import Footer from '../components/Footer';
import LeadFormPopup from '../components/LeadFormPopup';

const partners = [
  {
    name: 'RevoUltra',
    tagline: 'Impermeabilizzazione Definitiva',
    website: 'https://www.revoultra.it',
    logoType: 'text',
    description: 'RevoUltra e il sistema di impermeabilizzazione di ultima generazione basato su tecnologia nanopolimerica. Una membrana continua che crea una barriera molecolare impenetrabile, testata e certificata per durare oltre 25 anni.',
    highlights: [
      'Tecnologia nanopolimerica brevettata',
      'Applicazione su qualsiasi superficie: tegole, lamiera, cemento, guaine',
      'Nessuna demolizione necessaria',
      'Calpestabile in 60 secondi',
      'Resistente da -40°C a +200°C',
      'Protezione antigrandine certificata (65mm a 172 km/h)',
      'Garanzia scritta fino a 25 anni',
      'Certificazioni ETA, BBA, CE',
    ],
    stats: [
      { label: 'Anni di garanzia', value: '25' },
      { label: 'm² impermeabilizzati', value: '180K+' },
      { label: 'Clienti soddisfatti', value: '2.800+' },
    ],
  },
  {
    name: 'Isoliamo',
    tagline: 'Isolamento Tetti e Intercapedini',
    website: 'https://isoliamo.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/3qmu0ew3_5.png',
    description: 'Isoliamo è specializzata nell\u2019isolamento termico di tetti e intercapedini murarie con materiali certificati ad alta prestazione. Un intervento rapido, non invasivo, che ti permette di ridurre significativamente i consumi energetici della tua abitazione — sia in inverno che in estate — e aumentare il valore dell\u2019immobile.',
    highlights: [
      'Isolamento tetti dall\u2019interno o dall\u2019esterno',
      'Insufflaggio intercapedini murarie senza opere invasive',
      'Materiali certificati (fibra di legno, lana di roccia, sughero, EPS)',
      'Riduzione fino al 40% dei consumi energetici',
      'Miglior comfort abitativo estivo e invernale',
      'Consulenza su bonus fiscali ed ecobonus in vigore',
      'Sopralluogo e preventivo gratuito',
      'Certificazioni CE e conformità normativa vigente',
    ],
    stats: [
      { label: 'Riduzione consumi', value: 'fino 40%' },
      { label: 'Anni di esperienza', value: '10+' },
      { label: 'Immobili isolati', value: '1.200+' },
    ],
  },
  {
    name: 'RevoQuota',
    tagline: 'Specialisti del Lavoro in Quota',
    website: 'https://revoquota.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/r9hxdhmu_2BF8C848-2593-4E9F-9AAB-F6A193DD2537.jpeg',
    description: 'RevoQuota e la divisione operativa specializzata nel lavoro in quota. I nostri tecnici sono certificati e costantemente aggiornati sulle normative di sicurezza. Grazie ai sistemi di lavoro senza ponteggi, garantiamo interventi rapidi, sicuri e con un risparmio significativo.',
    highlights: [
      'Tecnici certificati per lavori in quota (D.Lgs. 81/2008)',
      'Sistemi di sicurezza anticaduta avanzati',
      'Formazione continua e aggiornamento normativo',
      'Operativita senza ponteggi: -70% sui costi',
      'Interventi su qualsiasi altezza e tipologia di edificio',
      'Assicurazione RC professionale completa',
    ],
    stats: [
      { label: 'Anni di esperienza', value: '15+' },
      { label: 'Interventi completati', value: '5.000+' },
      { label: 'Risparmio sui costi', value: '70%' },
    ],
  },
];

const RevoUltraLogo = () => (
  <div className="flex items-baseline gap-1">
    <span className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit' }}>
      Revo
    </span>
    <span className="text-3xl sm:text-4xl font-bold tracking-tight text-[#15803d]" style={{ fontFamily: 'Outfit' }}>
      Ultra
    </span>
    <span className="text-base text-[#15803d] font-semibold">®</span>
  </div>
);

const PartnerPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <DynamicIsland onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,87,34,0.04),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Il nostro network</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Aziende Partner
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Un ecosistema di aziende specializzate del gruppo RevoGreen per offrirti un servizio completo, dalla consulenza finanziaria all'intervento tecnico, fino alla garanzia a lungo termine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partners Detail */}
      {partners.map((partner, i) => (
        <section key={partner.name} className={`py-20 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center gap-5 mb-8">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-center shrink-0 min-h-[88px] min-w-[140px] shadow-sm">
                  {partner.logoType === 'text' ? (
                    <RevoUltraLogo />
                  ) : (
                    <img
                      src={partner.logoSrc}
                      alt={`${partner.name} logo`}
                      className="max-h-16 w-auto object-contain"
                    />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{partner.name}</h2>
                  <p className="text-[#FF5722] font-medium">{partner.tagline}</p>
                </div>
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-detail-link-${partner.name.toLowerCase()}`}
                  className="md:ml-auto inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#FF5722] transition-colors border border-slate-200 hover:border-[#FF5722]/30 px-4 py-2 rounded-full"
                >
                  Visita il sito <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mb-10 py-6 border-y border-slate-200">
                {partner.stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{stat.value}</div>
                    <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Description + Features */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-slate-600 leading-relaxed text-base">{partner.description}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {partner.highlights.map((h) => (
                    <div key={h} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Vuoi saperne di piu?</h2>
          <p className="text-slate-300 mb-8">Contattaci per scoprire come il nostro network puo aiutarti con il tuo progetto.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-8 py-4 rounded-full transition-all"
            >
              Contattaci
              <ArrowRight className="w-4 h-4" />
            </button>
            <a href="tel:800864621" className="inline-flex items-center gap-2 text-white border border-white/20 hover:border-white/40 px-8 py-4 rounded-full transition-all">
              <Phone className="w-4 h-4" />
              800 86 46 21
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <LeadFormPopup isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />

      {/* WhatsApp */}
      <a
        href="https://wa.me/393791617599"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default PartnerPage;
